import { useState, useEffect, useCallback, useRef, Component } from 'react';
import { getItems, createItem, updateItem } from './fitnessApi';
import { RESUME_TTL_MS } from './SwimmingWorkout';
import { RestClocksDisplay, formatRestMmSs } from './workoutTiming';
import { totalVolumeLb, latestWeightLbs, liftBurnRange, formatBurnRange, parseTemplateSets } from './liftingBurn';
import defineholyLogo from '../assets/defineholylogo.svg';

const TEMPLATES_LIST = 'LiftingTemplates';
const WORKOUTS_LIST = 'LiftingWorkouts';
const WEIGHINS_LIST = 'FitnessWeighIns';
const PERSIST_KEY = 'dh_liftWorkoutInProgress';

// Same accident guard as the pool: a SET DONE under this many seconds after
// START SET is a double-tap, not a set. The shortest real set here is a
// farmer carry, well over 20s.
const MIN_SET_SECONDS = 5;

// Rest-clock grain mapping. The shared RestClocksDisplay thinks in a fine
// rest and a coarse rest, keyed 'rep' and 'set'. In the pool those are
// rep/set; here the fine grain is BETWEEN SETS and the coarse grain is
// BETWEEN EXERCISES — rest between sets is the primary time datum in
// lifting, which is why the same capture carries over unchanged and only
// the labels move.
const REST_LABEL_FINE = 'SET';
const REST_LABEL_COARSE = 'EXER';

function safeParse(json) {
  try { return JSON.parse(json) || []; } catch { return []; }
}

// The prescription actually in force for an exercise entry: the primary, or
// its dumbbell alt overlaid when the swap is on. The alt inherits whatever
// it does not override — muscleGroups in particular, since a fallback
// movement targets the same muscles unless the template says otherwise.
function effectiveEntry(entry, useAlt) {
  if (!useAlt || !entry.alt || typeof entry.alt !== 'object') return entry;
  return {
    ...entry,
    exercise: entry.alt.exercise || entry.exercise,
    reps: entry.alt.reps ?? entry.reps,
    weightLb: entry.alt.weightLb !== undefined ? entry.alt.weightLb : null,
    muscleGroups: entry.alt.muscleGroups ?? entry.muscleGroups,
    notes: entry.alt.notes ?? entry.notes,
  };
}

// The RIR tap row's four options: stored value and gym-readable label.
// 2 covers "two or three left" — the capture is one tap, not a survey.
const RIR_OPTIONS = [
  { value: 0, label: 'FAIL' },
  { value: 1, label: '1 LEFT' },
  { value: 2, label: '2-3' },
  { value: 4, label: 'EASY' },
];

function LiftingWorkout({ getToken, userEmail }) {
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  // Template id being fresh-read by startWorkout; blocks a double tap and
  // labels the card while the read is in flight.
  const [startingId, setStartingId] = useState(null);
  const [error, setError] = useState(null);
  // Past workouts, newest first — the weight-prefill source. Weigh-ins feed
  // the decorative burn range on the summary and nothing else.
  const [pastWorkouts, setPastWorkouts] = useState([]);
  const [weighIns, setWeighIns] = useState([]);

  // Workout state. `sets` here is the exercise list; each entry is one
  // exercise prescription (sets × reps at a weight).
  const [workout, setWorkout] = useState(null); // { templateName, sets, startTime }
  const [currentIndex, setCurrentIndex] = useState(0);
  const [completedSets, setCompletedSets] = useState([]);
  const [showAddExercise, setShowAddExercise] = useState(false);
  const [newExercise, setNewExercise] = useState(null);
  const [showSummary, setShowSummary] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingProgress, setSavingProgress] = useState(false);
  const [workoutStartClock, setWorkoutStartClock] = useState(null); // ISO string — first START SET tap

  // Current exercise progress. setActive spans START SET → SET DONE; the
  // set itself is not timed (nobody times a squat rep — repTimes did not
  // transfer from the pool), the boundary taps exist to bound the RESTS.
  const [setsDone, setSetsDone] = useState(0);
  const [setActive, setSetActive] = useState(false);
  const setStartRef = useRef(null); // epoch ms of START SET, for the accident guard only

  // The per-exercise dumbbell swap. Reset on every index change: the swap
  // is a decision about THIS exercise today, not a sticky preference.
  const [useAlt, setUseAlt] = useState(false);

  // rirs[i] = reps-in-reserve tapped for set i+1, parallel to the set count
  // the way repRests parallels it. Null when the tap was skipped — SET DONE
  // never blocks on it, and null stays null all the way to the saved row:
  // unknown, not zero. Same discipline as the food module.
  const [rirs, setRirs] = useState([]);

  // Editable prescription for the exercise on screen. Weight prefills from
  // the template (or the last workout that lifted this exercise) and what
  // gets typed here is what the saved row carries.
  const [weightInput, setWeightInput] = useState('');
  const [repsInput, setRepsInput] = useState('');

  // Actual rest, measured wall-clock. repRests[i] is the rest taken BEFORE
  // set i+1 of the current exercise, so repRests[0] is the inter-exercise
  // rest — lastSetStopRef deliberately survives exercise boundaries and is
  // cleared only by startWorkout. The workout's very first set has no prior
  // stop, so it records null. Same parallel-array contract as the pool.
  const [repRests, setRepRests] = useState([]);
  const [repRestTargets, setRepRestTargets] = useState([]);
  const lastSetStopRef = useRef(null);

  // Rest count-up state — identical mechanics to the pool, relabeled.
  const [restMode, setRestMode] = useState(null); // null | 'rep' (between sets) | 'set' (between exercises)
  const [repRestFrozen, setRepRestFrozen] = useState(null);
  const [setRestFrozen, setSetRestFrozen] = useState(null);
  const [repTarget, setRepTarget] = useState(90);
  const [setTarget, setSetTarget] = useState(120);
  const [adjMode, setAdjMode] = useState('rep');
  const restElapsedRef = useRef(0);
  const loadedTargetIndexRef = useRef(null);
  const wakeLockRef = useRef(null);
  const persistRef = useRef({});
  const [resumeCandidate, setResumeCandidate] = useState(null);
  const [justResumed, setJustResumed] = useState(false);
  const [savedWorkoutId, setSavedWorkoutId] = useState(null);
  const [toast, setToast] = useState(null);

  const restoreSession = useCallback((saved) => {
    setWorkout(saved.workout);
    setCurrentIndex(saved.currentIndex);
    setCompletedSets(saved.completedSets);
    setSetsDone(saved.setsDone ?? 0);
    // setActive is deliberately NOT restored: a set that spanned a reload
    // has no honest boundary taps left, and resuming mid-"active" would
    // record a rest that never happened. The set still counts when SET DONE
    // or Next Exercise is tapped.
    setWeightInput(saved.weightInput ?? '');
    setRepsInput(saved.repsInput ?? '');
    setUseAlt(saved.useAlt ?? false);
    setRirs(saved.rirs ?? []);
    setRepRests(saved.repRests ?? []);
    setRepRestTargets(saved.repRestTargets ?? []);
    lastSetStopRef.current = saved.lastSetStop ?? null;
    setWorkoutStartClock(saved.workoutStartClock);
    setSavedWorkoutId(saved.savedWorkoutId ?? null);
    setRepTarget(saved.repTarget ?? 90);
    setSetTarget(saved.setTarget ?? 120);
    loadedTargetIndexRef.current = saved.currentIndex;
    setJustResumed(true);
  }, []);

  // Check for a saved in-progress workout on mount
  useEffect(() => {
    const autoResume = localStorage.getItem('dh_liftAutoResume');
    localStorage.removeItem('dh_liftAutoResume');
    const raw = localStorage.getItem(PERSIST_KEY);
    if (!raw) return;
    try {
      const saved = JSON.parse(raw);
      if (Date.now() - saved.savedAt < RESUME_TTL_MS) {
        if (autoResume) {
          localStorage.removeItem(PERSIST_KEY);
          restoreSession(saved);
        } else {
          setResumeCandidate(saved);
        }
      } else {
        localStorage.removeItem(PERSIST_KEY);
      }
    } catch {
      localStorage.removeItem(PERSIST_KEY);
    }
  }, [restoreSession]);

  useEffect(() => {
    if (!justResumed) return;
    const id = setTimeout(() => setJustResumed(false), 8000);
    return () => clearTimeout(id);
  }, [justResumed]);

  // Wake lock while a workout is active; re-acquired on visibility return.
  useEffect(() => {
    if (!workout) return;
    if (navigator.wakeLock) {
      navigator.wakeLock.request('screen')
        .then(s => { wakeLockRef.current = s; })
        .catch(() => {});
    }
    return () => {
      wakeLockRef.current?.release().catch(() => {});
      wakeLockRef.current = null;
    };
  }, [workout]);

  useEffect(() => {
    if (!workout) return;
    const reacquire = () => {
      if (document.visibilityState === 'visible' && navigator.wakeLock) {
        navigator.wakeLock.request('screen')
          .then(s => { wakeLockRef.current = s; })
          .catch(() => {});
      }
    };
    document.addEventListener('visibilitychange', reacquire);
    return () => document.removeEventListener('visibilitychange', reacquire);
  }, [workout]);

  // Persist on every relevant change, flush on pagehide — same double door
  // as the pool.
  useEffect(() => {
    if (!workout) return;
    try {
      localStorage.setItem(PERSIST_KEY, JSON.stringify({
        workout, currentIndex, completedSets, setsDone,
        weightInput, repsInput, useAlt, rirs,
        repRests, repRestTargets, lastSetStop: lastSetStopRef.current,
        repTarget, setTarget, workoutStartClock, savedWorkoutId,
        savedAt: Date.now(),
      }));
    } catch (e) {
      console.warn('Lift persist failed:', e);
    }
  }, [workout, currentIndex, completedSets, setsDone, weightInput, repsInput, useAlt, rirs, repRests, repRestTargets, repTarget, setTarget, workoutStartClock, savedWorkoutId]);

  useEffect(() => {
    const flush = () => {
      const s = persistRef.current;
      if (!s.workout) return;
      try {
        localStorage.setItem(PERSIST_KEY, JSON.stringify({ ...s, savedAt: Date.now() }));
      } catch (e) {
        console.warn('Lift flush failed:', e);
      }
    };
    document.addEventListener('visibilitychange', flush);
    window.addEventListener('pagehide', flush);
    return () => {
      document.removeEventListener('visibilitychange', flush);
      window.removeEventListener('pagehide', flush);
    };
  }, []);

  // When a new exercise becomes current: adopt its prescribed rest and its
  // prescription into the inputs, and drop any dumbbell swap — the swap is
  // per-exercise, per-day. Guarded by index so a re-render from an
  // unrelated workout change can't clobber a mid-exercise ±15 override or a
  // typed weight. Never resets on null workout — restoreSession claims the
  // index first, same ordering as the pool.
  useEffect(() => {
    if (!workout) return;
    if (loadedTargetIndexRef.current === currentIndex) return;
    loadedTargetIndexRef.current = currentIndex;
    const entry = workout.sets[currentIndex];
    if (!entry) return;
    setUseAlt(false);
    const prescribed = Number(entry.restSeconds);
    if (prescribed > 0) setRepTarget(prescribed);
    setWeightInput(entry.weightLb === null || entry.weightLb === undefined ? '' : String(entry.weightLb));
    setRepsInput(entry.reps === null || entry.reps === undefined ? '' : String(entry.reps));
  }, [workout, currentIndex]);

  const fetchData = useCallback(async () => {
    try {
      setError(null);
      const token = await getToken();
      const [tmpl, past, wi] = await Promise.all([
        getItems(token, TEMPLATES_LIST),
        getItems(token, WORKOUTS_LIST),
        getItems(token, WEIGHINS_LIST),
      ]);
      setTemplates(
        tmpl.map(it => ({
          id: it.id,
          name: it.Title || '',
          description: it.Description || '',
          sets: parseTemplateSets(it.Sets),
        }))
      );
      setPastWorkouts(
        past
          .filter(w => w.CreatedByEmail === userEmail)
          .map(w => ({ sets: safeParse(w.Sets), date: w.WorkoutDate || '' }))
          .sort((a, b) => new Date(b.date) - new Date(a.date))
      );
      setWeighIns(wi.filter(w => w.CreatedByEmail === userEmail));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingTemplates(false);
    }
  }, [getToken, userEmail]);

  useEffect(() => { fetchData(); }, [fetchData]);

  function showToast(message) {
    setToast(message);
    setTimeout(() => setToast(null), 2000);
  }

  // The template's weight wins when it has one — editing the template is a
  // deliberate override. Otherwise the most recent workout that lifted this
  // exercise (by name) supplies it: fill a weight in during the first run
  // and it IS the prefill next session, no template edit needed.
  function prefillWeight(exerciseName) {
    const wanted = (exerciseName || '').trim().toLowerCase();
    for (const w of pastWorkouts) {
      for (const s of w.sets) {
        if ((s.exercise || '').trim().toLowerCase() !== wanted) continue;
        const lb = Number(s.weightLb);
        if (Number.isFinite(lb) && lb > 0) return lb;
      }
    }
    return null;
  }

  // The prescription is built from a FRESH read of the template row at tap
  // time, never from the list fetched at mount. A template edited after
  // this screen mounted — the Templates tab in another window, an MCP
  // patch, a reseed — would otherwise start a stale workout with stale
  // weights, and the em dash it produces is indistinguishable from a
  // template that was never filled in. The mount-time copy remains the
  // fallback when the read fails: at the gym, a cached prescription beats
  // no workout.
  async function startWorkout(tmpl) {
    if (startingId !== null) return;
    setStartingId(tmpl.id);
    let templateSets = tmpl.sets;
    try {
      const token = await getToken();
      const rows = await getItems(token, TEMPLATES_LIST);
      const current = rows.find(r => r.id === tmpl.id);
      if (current) templateSets = parseTemplateSets(current.Sets);
    } catch { /* stale beats nothing */ } finally {
      setStartingId(null);
    }
    const prescription = templateSets.map(s => ({
      ...s,
      weightLb: Number.isFinite(Number(s.weightLb)) && s.weightLb !== null && s.weightLb !== ''
        ? Number(s.weightLb)
        : prefillWeight(s.exercise),
    }));
    if (prescription.length === 0) {
      setError('This template has no exercises.');
      return;
    }
    setWorkout({ templateName: tmpl.name, sets: prescription, startTime: Date.now() });
    setCurrentIndex(0);
    setCompletedSets([]);
    setShowSummary(false);
    setSetsDone(0);
    setSetActive(false);
    setUseAlt(false);
    setStartRef.current = null;
    lastSetStopRef.current = null;
    loadedTargetIndexRef.current = null; // let exercise 0 load its prescription
    setRirs([]);
    setRepRests([]);
    setRepRestTargets([]);
    setWorkoutStartClock(null);
    clearRestCountUp();
    setRepRestFrozen(null);
    setSetRestFrozen(null);
    setAdjMode('rep');
  }

  // The dumbbell swap, mid-workout. Re-prefills the inputs from the alt's
  // prescription (template alt weight, else the last workout that lifted
  // the ALT exercise) — and back again on un-swap. Locked once any set of
  // this exercise is done or active: half-barbell half-dumbbell would save
  // as one exercise name that only lifted half the sets.
  function toggleAlt() {
    const entry = currentEntry();
    if (!entry || !entry.alt || setsDone > 0 || setActive) return;
    const next = !useAlt;
    setUseAlt(next);
    const eff = effectiveEntry(entry, next);
    const weight = Number.isFinite(Number(eff.weightLb)) && eff.weightLb !== null && eff.weightLb !== ''
      ? Number(eff.weightLb)
      : prefillWeight(eff.exercise);
    setWeightInput(weight === null || weight === undefined ? '' : String(weight));
    setRepsInput(eff.reps === null || eff.reps === undefined ? '' : String(eff.reps));
  }

  function currentEntry() {
    if (!workout || currentIndex >= workout.sets.length) return null;
    return workout.sets[currentIndex];
  }

  function nextEntry() {
    if (!workout || currentIndex + 1 >= workout.sets.length) return null;
    return workout.sets[currentIndex + 1];
  }

  function clearRestCountUp() {
    setRestMode(null);
  }

  function startRestCountUp(mode) {
    restElapsedRef.current = 0;
    setRestMode(mode);
    setAdjMode(mode);
  }

  // START SET — the rest that was running ends here, and is recorded
  // against the target that was in force while it was being taken. Same
  // capture as the pool's startStopwatch, minus the stopwatch.
  function handleStartSet() {
    setJustResumed(false);
    if (restMode === 'rep') {
      setRepRestFrozen(restElapsedRef.current);
      clearRestCountUp();
    } else if (restMode === 'set') {
      setSetRestFrozen(restElapsedRef.current);
      setRepRestFrozen(null);
      clearRestCountUp();
    }
    const now = Date.now();
    const hadPriorStop = lastSetStopRef.current != null;
    setRepRests(prev => [...prev, hadPriorStop
      ? Math.round(((now - lastSetStopRef.current) / 1000) * 10) / 10
      : null]);
    setRepRestTargets(prev => [...prev, hadPriorStop
      ? (restMode === 'set' ? setTarget : repTarget)
      : null]);
    setStartRef.current = now;
    setSetActive(true);
    if (!workoutStartClock) {
      setWorkoutStartClock(new Date().toISOString());
    }
  }

  // SET DONE — the set ends, the rest clock starts. A rejected tap leaves
  // everything untouched: the set stays active, because the tap was the
  // accident, not the set.
  function handleSetDone() {
    if (!setActive) return;
    const elapsed = setStartRef.current ? (Date.now() - setStartRef.current) / 1000 : Infinity;
    if (elapsed < MIN_SET_SECONDS) {
      showToast(`Tap ignored — under ${MIN_SET_SECONDS}s`);
      return;
    }
    lastSetStopRef.current = Date.now();
    setSetActive(false);
    const entry = currentEntry();
    const plannedSets = Number(entry?.sets) || 1;
    const done = setsDone + 1;
    setSetsDone(done);
    // The set's RIR slot opens as null — unknown until tapped, and it may
    // stay unknown. Never a zero: 0 means failure, not "no answer".
    setRirs(prev => [...prev, null]);
    // The break after an exercise's last set is an EXERCISE rest, not a set
    // rest — same boundary rule as the pool's set clock.
    startRestCountUp(done >= plannedSets ? 'set' : 'rep');
  }

  // One tap on the RIR row rates the set that just finished. Re-tappable
  // until the next set starts; tapping the selected value clears it back to
  // unknown rather than locking a misttap in.
  function rateRir(value) {
    if (setsDone === 0) return;
    setRirs(prev => {
      const next = [...prev];
      next[setsDone - 1] = next[setsDone - 1] === value ? null : value;
      return next;
    });
  }

  // One committed entry for the exercise on screen, built from the inputs
  // and the counted sets. Pure — used by both the commit path and the
  // save-progress snapshot. The dumbbell swap resolves HERE: what saves is
  // the exercise actually performed, alt name and alt muscles included.
  function buildEntry(entry, countedSets, isPartial) {
    const eff = effectiveEntry(entry, useAlt);
    const plannedSets = Number(eff.sets) || 1;
    const weight = weightInput === '' ? null : Number(weightInput);
    const reps = repsInput === '' ? (Number(eff.reps) || null) : Number(repsInput);
    // rirs padded to the counted sets: a set counted by a mid-set Done tap
    // never had a SET DONE, so its slot is null — unknown, not zero.
    const paddedRirs = Array.from({ length: countedSets }, (_, i) => rirs[i] ?? null);
    return {
      exercise: eff.exercise,
      weightLb: Number.isFinite(weight) ? weight : null,
      reps: eff.reps,
      actualReps: Number.isFinite(reps) ? reps : null,
      sets: eff.sets,
      actualSets: countedSets,
      muscleGroups: Array.isArray(eff.muscleGroups) ? eff.muscleGroups : undefined,
      sectionHeader: eff.sectionHeader || '',
      notes: eff.notes || '',
      restSeconds: eff.restSeconds,
      rirs: countedSets > 0 ? paddedRirs : undefined,
      repRests: repRests.length > 0 ? repRests : undefined,
      repRestTargets: repRestTargets.length > 0 ? repRestTargets : undefined,
      setTargetUsed: setTarget,
      ...(entry.unplanned ? { unplanned: true } : {}),
      ...(isPartial || countedSets < plannedSets ? { partial: true } : {}),
    };
  }

  function handleDoneExercise() {
    setJustResumed(false);
    const entry = currentEntry();
    if (!entry) return;

    // Done tapped mid-set: that set ends now and counts. Without the stamp
    // the next exercise's first rest would wrongly include this set's work.
    let counted = setsDone;
    if (setActive) {
      counted += 1;
      lastSetStopRef.current = Date.now();
      setSetActive(false);
    }

    setCompletedSets(prev => [...prev, buildEntry(entry, counted, false)]);
    setSetsDone(0);
    setUseAlt(false);
    setRirs([]);
    setRepRests([]);
    setRepRestTargets([]);
    setStartRef.current = null;

    const nextIdx = currentIndex + 1;
    if (nextIdx >= workout.sets.length) {
      clearRestCountUp();
      setShowSummary(true);
      return;
    }

    if (restMode === 'rep') setRepRestFrozen(restElapsedRef.current);
    // Idempotent, same as the pool: after the final set the EXER clock is
    // already running and has been accumulating since that set's SET DONE.
    if (restMode !== 'set') {
      startRestCountUp('set');
    } else {
      setAdjMode('set');
    }
    setCurrentIndex(nextIdx);
  }

  function handleAddExercise() {
    setNewExercise({ exercise: '', sets: 3, reps: 8, weightLb: '', restSeconds: 90, notes: '' });
    setShowAddExercise(true);
  }

  function confirmAddExercise() {
    if (!newExercise || !newExercise.exercise.trim()) return;
    setWorkout(prev => {
      const sets = [...prev.sets];
      sets.splice(currentIndex + 1, 0, {
        ...newExercise,
        exercise: newExercise.exercise.trim(),
        weightLb: newExercise.weightLb === '' ? null : Number(newExercise.weightLb),
        unplanned: true,
      });
      return { ...prev, sets };
    });
    setShowAddExercise(false);
    setNewExercise(null);
  }

  // completedSets plus the in-progress exercise, if any work happened on it.
  // Mirrors the pool's buildEffectiveCompletedSets: pure snapshot, the live
  // workout continues unchanged.
  function buildEffectiveCompletedSets() {
    const entry = currentEntry();
    const counted = setsDone + (setActive ? 1 : 0);
    if (!entry || counted === 0) return completedSets;
    return [...completedSets, buildEntry(entry, counted, true)];
  }

  function buildSaveFields(effectiveSets) {
    const endTime = new Date().toISOString();
    const durationSec = workoutStartClock
      ? Math.round((new Date(endTime) - new Date(workoutStartClock)) / 1000)
      : Math.round((Date.now() - workout.startTime) / 1000);
    return {
      Title: `${workout.templateName} - ${new Date().toLocaleDateString()}`,
      TemplateName: workout.templateName,
      Sets: JSON.stringify(effectiveSets),
      TotalVolumeLb: Math.round(totalVolumeLb(effectiveSets)),
      DurationSeconds: durationSec,
      WorkoutDate: new Date().toISOString(),
      CreatedByEmail: userEmail,
      WorkoutStartTime: workoutStartClock || '',
      WorkoutEndTime: endTime,
    };
  }

  async function saveProgress() {
    const effectiveSets = buildEffectiveCompletedSets();
    if (effectiveSets.length === 0) return;
    setSavingProgress(true);
    try {
      const token = await getToken();
      const fields = buildSaveFields(effectiveSets);
      if (savedWorkoutId) {
        await updateItem(token, WORKOUTS_LIST, savedWorkoutId, fields);
      } else {
        const created = await createItem(token, WORKOUTS_LIST, fields);
        setSavedWorkoutId(created.id);
      }
      showToast('Saved ✓');
    } catch (e) {
      console.warn('Save progress failed:', e);
    } finally {
      setSavingProgress(false);
    }
  }

  function clearWorkoutState() {
    clearRestCountUp();
    setRepRestFrozen(null);
    setSetRestFrozen(null);
    setAdjMode('rep');
    setSetsDone(0);
    setSetActive(false);
    setUseAlt(false);
    setStartRef.current = null;
    setRirs([]);
    setRepRests([]);
    setRepRestTargets([]);
    setWorkout(null);
    setShowSummary(false);
    setCompletedSets([]);
    setSavedWorkoutId(null);
    localStorage.removeItem(PERSIST_KEY);
  }

  async function saveWorkout() {
    setSaving(true);
    setError(null);
    try {
      const token = await getToken();
      const effectiveSets = buildEffectiveCompletedSets();
      const fields = buildSaveFields(effectiveSets);
      if (savedWorkoutId) {
        await updateItem(token, WORKOUTS_LIST, savedWorkoutId, fields);
      } else {
        await createItem(token, WORKOUTS_LIST, fields);
      }
      clearWorkoutState();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  async function saveAndEnd() {
    const effectiveSets = buildEffectiveCompletedSets();
    if (effectiveSets.length === 0) {
      discardWorkout();
      return;
    }
    await saveWorkout();
  }

  function discardWorkout() {
    if (completedSets.length > 0 && !window.confirm("Are you sure? This workout won't be recorded.")) return;
    clearWorkoutState();
  }

  function handleResume() {
    restoreSession(resumeCandidate);
    setResumeCandidate(null);
    localStorage.removeItem(PERSIST_KEY);
  }

  function handleDiscardResume() {
    localStorage.removeItem(PERSIST_KEY);
    setResumeCandidate(null);
  }

  // Keep persistRef current so the flush handler never reads stale state
  persistRef.current = { workout, currentIndex, completedSets, setsDone, weightInput, repsInput, useAlt, rirs, repRests, repRestTargets, lastSetStop: lastSetStopRef.current, repTarget, setTarget, workoutStartClock, savedWorkoutId };

  // --- RENDER ---

  if (resumeCandidate && !workout) {
    const minAgo = Math.round((Date.now() - resumeCandidate.savedAt) / 60000);
    const agoLabel = minAgo < 1 ? 'just now' : minAgo === 1 ? '1 minute ago' : `${minAgo} minutes ago`;
    return (
      <div className="SwimWorkout">
        <div className="SwimWorkout-resume-prompt">
          <h3 className="SwimWorkout-resume-heading">Workout In Progress</h3>
          <p className="SwimWorkout-resume-name">{resumeCandidate.workout.templateName}</p>
          <p className="SwimWorkout-resume-meta">
            Exercise {resumeCandidate.currentIndex + 1} of {resumeCandidate.workout.sets.length}
            &nbsp;&middot;&nbsp;
            {resumeCandidate.completedSets.length} exercise{resumeCandidate.completedSets.length !== 1 ? 's' : ''} completed
          </p>
          <p className="SwimWorkout-resume-time">Saved {agoLabel}</p>
          <div className="SwimWorkout-resume-actions">
            <button className="Fitness-btn" onClick={handleResume}>Resume</button>
            <button className="Fitness-btn Fitness-btn--secondary" onClick={handleDiscardResume}>Start Fresh</button>
          </div>
        </div>
      </div>
    );
  }

  // Workout summary
  if (showSummary && workout) {
    const volume = totalVolumeLb(completedSets);
    const elapsed = workoutStartClock
      ? Math.round((Date.now() - new Date(workoutStartClock).getTime()) / 1000)
      : Math.round((Date.now() - workout.startTime) / 1000);
    const elapsedMin = Math.floor(elapsed / 60);
    const elapsedSec = elapsed % 60;
    const startTimeDisplay = workoutStartClock
      ? new Date(workoutStartClock).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
      : null;
    const burn = liftBurnRange(latestWeightLbs(weighIns), elapsed);
    return (
      <div className="SwimWorkout-summary">
        <h2>Workout Complete</h2>
        {error && <p className="Fitness-error">{error}</p>}
        <div className="SwimWorkout-summary-stats">
          <div className="SwimWorkout-summary-stat">
            <span className="SwimWorkout-summary-label">Total Volume</span>
            <span className="SwimWorkout-summary-value">{Math.round(volume).toLocaleString('en-US')} lb</span>
          </div>
          <div className="SwimWorkout-summary-stat">
            <span className="SwimWorkout-summary-label">Exercises</span>
            <span className="SwimWorkout-summary-value">{completedSets.length}</span>
          </div>
          <div className="SwimWorkout-summary-stat">
            <span className="SwimWorkout-summary-label">Total Time</span>
            <span className="SwimWorkout-summary-value">{elapsedMin}:{String(elapsedSec).padStart(2, '0')}</span>
          </div>
          {startTimeDisplay && (
            <div className="SwimWorkout-summary-stat">
              <span className="SwimWorkout-summary-label">Started</span>
              <span className="SwimWorkout-summary-value">{startTimeDisplay}</span>
            </div>
          )}
        </div>

        {/* The burn range renders like the swim card's and drives nothing —
            see liftingBurn.js for why it is decorative. Absent weigh-in,
            absent line. */}
        {burn && <p className="LiftWorkout-burn">{formatBurnRange(burn)}</p>}

        <div className="SwimWorkout-summary-sets">
          {completedSets.map((s, i) => (
            <div key={i} className={`SwimWorkout-summary-set${s.unplanned ? ' SwimWorkout-summary-set--unplanned' : ''}`}>
              {s.sectionHeader && <div className="SwimWorkout-section-label">{s.sectionHeader}</div>}
              <span>
                {s.exercise} · {s.actualSets}x{s.actualReps ?? '—'}
                {Number.isFinite(Number(s.weightLb)) && s.weightLb !== null ? ` @ ${s.weightLb} lb` : ''}
                {s.partial ? ' (partial)' : ''}
              </span>
              {s.notes && <span className="SwimWorkout-set-notes">{s.notes}</span>}
              {s.unplanned && <span className="SwimWorkout-set-tag">added</span>}
            </div>
          ))}
        </div>

        <div className="SwimWorkout-summary-actions">
          <button className="Fitness-btn" onClick={saveWorkout} disabled={saving}>
            {saving ? 'Saving...' : 'Save Workout'}
          </button>
          <button className="Fitness-btn Fitness-btn--secondary" onClick={discardWorkout}>Discard</button>
        </div>
      </div>
    );
  }

  // Active workout
  if (workout) {
    const entry = currentEntry();
    const next = nextEntry();
    if (!entry) return null;
    // Everything on screen renders the EFFECTIVE prescription — the alt when
    // swapped — so what the eyes confirm is what buildEntry saves.
    const eff = effectiveEntry(entry, useAlt);
    const canSwap = !!(entry.alt && entry.alt.exercise) && setsDone === 0 && !setActive;
    const progress = `${currentIndex + 1} / ${workout.sets.length}`;
    const runningVolume = totalVolumeLb(buildEffectiveCompletedSets());
    const plannedSets = Number(eff.sets) || 1;
    const setBadgeClass = `SwimWorkout-rep-badge${
      setsDone + 1 >= plannedSets ? ' SwimWorkout-rep-badge--red'
      : setsDone + 2 === plannedSets ? ' SwimWorkout-rep-badge--amber'
      : ''
    }`;

    return (
      <div className="SwimWorkout-poolside">
        <div className="SwimWorkout-poolside-header">
          <span className="SwimWorkout-poolside-progress">{progress}</span>
          <span className="SwimWorkout-poolside-running">{Math.round(runningVolume).toLocaleString('en-US')} lb</span>
        </div>

        {justResumed && (
          <div
            className="SwimWorkout-poolside-resumed-banner"
            onClick={() => setJustResumed(false)}
          >
            RESUMED — Exercise {currentIndex + 1} of {workout.sets.length} · {completedSets.length} logged
          </div>
        )}

        {toast && (
          <div className="SwimWorkout-poolside-resumed-banner SwimWorkout-poolside-saved-toast">
            {toast}
          </div>
        )}

        {eff.sectionHeader && <div className="SwimWorkout-poolside-section">{eff.sectionHeader}</div>}

        <div className="SwimWorkout-poolside-main">
          <div className="SwimWorkout-poolside-reps-wrap">
            <img src={defineholyLogo} alt="Define Holy" className="SwimWorkout-poolside-logo" />
            <div className="LiftWorkout-exercise">{eff.exercise}</div>
          </div>
          <div className="SwimWorkout-poolside-stroke">
            {eff.sets} x {eff.reps}
          </div>
          {eff.notes && <div className="SwimWorkout-poolside-notes">{eff.notes}</div>}
          {eff.restSeconds && <div className="SwimWorkout-poolside-rest-info">Rest: {formatRestMmSs(Number(eff.restSeconds))}</div>}

          {/* The dumbbell fallback, one tap away — never a prompt. Locked
              once a set is underway or done: half-and-half would save as one
              exercise that only lifted half its sets. */}
          {entry.alt && entry.alt.exercise && (
            <button
              type="button"
              className="Fitness-btn Fitness-btn--small Fitness-btn--secondary LiftWorkout-swap"
              onClick={toggleAlt}
              disabled={!canSwap}
              title={canSwap ? undefined : 'Swap locks once a set is underway'}
            >
              {useAlt ? `↩ ${entry.exercise}` : `⇄ ${entry.alt.exercise}`}
            </button>
          )}

          {/* The prescription, editable in place. Weight typed here is what
              the saved row carries — and what prefills next session. */}
          <div className="LiftWorkout-inputs">
            <label className="LiftWorkout-input-label">
              <span>Weight (lb)</span>
              <input
                className="Fitness-input LiftWorkout-input"
                type="number" min="0" step="2.5" inputMode="decimal"
                value={weightInput}
                onChange={e => setWeightInput(e.target.value)}
                placeholder="—"
              />
            </label>
            <label className="LiftWorkout-input-label">
              <span>Reps</span>
              <input
                className="Fitness-input LiftWorkout-input"
                type="number" min="0" inputMode="numeric"
                value={repsInput}
                onChange={e => setRepsInput(e.target.value)}
              />
            </label>
          </div>

          <div className="SwimWorkout-poolside-rep-counter">
            Set {Math.min(setsDone + 1, plannedSets)} of {plannedSets}
          </div>

          <div className={setBadgeClass}>
            <div className="SwimWorkout-rep-badge__digit">{Math.min(setsDone + 1, plannedSets)}</div>
            <div className="SwimWorkout-rep-badge__total">/{plannedSets}</div>
          </div>

          {/* Completed sets of this exercise, with the rest that preceded
              each and the RIR it was rated — the rest is the datum here. */}
          {setsDone > 0 && (
            <div className="SwimWorkout-poolside-rep-times">
              {Array.from({ length: setsDone }, (_, i) => (
                <span key={i} className="SwimWorkout-poolside-rep-time">
                  S{i + 1} ✓{repRests[i] != null ? ` (rest ${formatRestMmSs(repRests[i])})` : ''}{rirs[i] != null ? ` RIR ${rirs[i]}` : ''}
                </span>
              ))}
            </div>
          )}

          {/* One tap rates the set that just finished. Optional by design —
              SET DONE never waits on it, and a skipped tap stays null all
              the way to the saved row: unknown, not zero. */}
          {setsDone > 0 && (
            <div className="LiftWorkout-rir-row">
              <span className="LiftWorkout-rir-label">HOW HARD? S{setsDone}</span>
              {RIR_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  type="button"
                  className={`Fitness-btn Fitness-btn--small LiftWorkout-rir-btn${rirs[setsDone - 1] === opt.value ? ' Fitness-btn--active' : ''}`}
                  onClick={() => rateRir(opt.value)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {next && (
          <div className="SwimWorkout-poolside-next">
            <span className="SwimWorkout-poolside-next-label">Next:</span>
            <span>{next.exercise} {next.sets}x{next.reps}</span>
          </div>
        )}

        <RestClocksDisplay
          restMode={restMode}
          frozenRepRest={repRestFrozen}
          frozenSetRest={setRestFrozen}
          repTarget={repTarget}
          setTarget={setTarget}
          adjMode={adjMode}
          setRepTarget={setRepTarget}
          setSetTarget={setSetTarget}
          setAdjMode={setAdjMode}
          elapsedRef={restElapsedRef}
          repLabel={REST_LABEL_FINE}
          setLabel={REST_LABEL_COARSE}
        />

        <div className="SwimWorkout-poolside-stopwatch-actions">
          {!setActive && setsDone < plannedSets && (
            <button className="Fitness-btn Fitness-btn--poolside SwimWorkout-stopwatch-start" onClick={handleStartSet}>
              START SET
            </button>
          )}
          {setActive && (
            <button className="Fitness-btn Fitness-btn--poolside SwimWorkout-stopwatch-stop" onClick={handleSetDone}>
              SET DONE
            </button>
          )}
        </div>

        <div className="SwimWorkout-poolside-actions">
          <button className="Fitness-btn Fitness-btn--poolside Fitness-btn--done" onClick={handleDoneExercise}>
            Next Exercise
          </button>
          <button className="Fitness-btn Fitness-btn--secondary Fitness-btn--poolside-sm" onClick={handleAddExercise}>+ Exercise</button>
        </div>

        <div className="SwimWorkout-poolside-end-actions">
          <button
            className="Fitness-btn Fitness-btn--secondary Fitness-btn--poolside-sm SwimWorkout-poolside-progress-save"
            onClick={saveProgress}
            disabled={savingProgress || saving || (completedSets.length === 0 && setsDone === 0 && !setActive)}
          >
            {savingProgress ? 'Saving...' : 'Save Progress'}
          </button>
          <button className="Fitness-btn Fitness-btn--poolside-sm SwimWorkout-poolside-save" onClick={saveAndEnd} disabled={saving || savingProgress}>
            {saving ? 'Saving...' : 'Save & End'}
          </button>
          <button className="Fitness-btn Fitness-btn--secondary Fitness-btn--small SwimWorkout-poolside-discard" onClick={discardWorkout}>
            Discard
          </button>
        </div>

        {showAddExercise && newExercise && (
          <div className="SwimWorkout-add-overlay">
            <h3>Add Exercise</h3>
            <div className="SwimWorkout-add-fields">
              <label>Exercise <input className="Fitness-input" type="text" value={newExercise.exercise}
                onChange={e => setNewExercise(p => ({ ...p, exercise: e.target.value }))} /></label>
              <label>Sets <input className="Fitness-input" type="number" min="1" value={newExercise.sets}
                onChange={e => setNewExercise(p => ({ ...p, sets: parseInt(e.target.value) || 1 }))} /></label>
              <label>Reps <input className="Fitness-input" type="number" min="1" value={newExercise.reps}
                onChange={e => setNewExercise(p => ({ ...p, reps: parseInt(e.target.value) || 1 }))} /></label>
              <label>Weight (lb) <input className="Fitness-input" type="number" min="0" step="2.5" value={newExercise.weightLb}
                onChange={e => setNewExercise(p => ({ ...p, weightLb: e.target.value }))} /></label>
              <label>Rest (sec) <input className="Fitness-input" type="number" min="0" step="15" value={newExercise.restSeconds}
                onChange={e => setNewExercise(p => ({ ...p, restSeconds: parseInt(e.target.value) || 0 }))} /></label>
              <label>Notes <input className="Fitness-input" type="text" value={newExercise.notes}
                onChange={e => setNewExercise(p => ({ ...p, notes: e.target.value }))} /></label>
            </div>
            <div className="SwimWorkout-add-actions">
              <button className="Fitness-btn" onClick={confirmAddExercise}>Add</button>
              <button className="Fitness-btn Fitness-btn--secondary" onClick={() => setShowAddExercise(false)}>Cancel</button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Template selection — no equipment prompt: barbell is primary, the
  // dumbbell fallback is a per-exercise swap inside the runner.
  return (
    <div className="SwimWorkout">
      <h3>Select a Template</h3>
      {error && <p className="Fitness-error">{error}</p>}
      {loadingTemplates && <p className="Fitness-loading">Loading templates...</p>}
      {!loadingTemplates && templates.length === 0 && (
        <p className="Fitness-empty">No templates yet. Go to Templates tab to create one.</p>
      )}
      <div className="SwimWorkout-template-list">
        {templates.map(tmpl => (
          <div key={tmpl.id} className="SwimWorkout-template-card" onClick={() => startWorkout(tmpl)}>
            <h4>{tmpl.name}</h4>
            <span>
              {startingId === tmpl.id
                ? 'Starting...'
                : `${tmpl.sets.length} exercise${tmpl.sets.length !== 1 ? 's' : ''}`}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

class LiftErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, remountKey: 0 };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error) {
    console.warn('LiftingWorkout crashed, session preserved in localStorage:', error);
  }

  handleResume() {
    try { localStorage.setItem('dh_liftAutoResume', '1'); } catch (e) { console.warn('localStorage write failed:', e); }
    this.setState(s => ({ hasError: false, remountKey: s.remountKey + 1 }));
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="SwimWorkout">
          <div className="SwimWorkout-resume-prompt">
            <h3 className="SwimWorkout-resume-heading">Workout interrupted</h3>
            <p className="SwimWorkout-resume-meta">Your progress is saved.</p>
            <div className="SwimWorkout-resume-actions">
              <button className="Fitness-btn" onClick={() => this.handleResume()}>Resume Workout</button>
            </div>
          </div>
        </div>
      );
    }
    return <div key={this.state.remountKey}>{this.props.children}</div>;
  }
}

function LiftingWorkoutWithBoundary(props) {
  return <LiftErrorBoundary><LiftingWorkout {...props} /></LiftErrorBoundary>;
}

export default LiftingWorkoutWithBoundary;
